import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostBinding,
	inject,
	Input,
	input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewRef,
} from "@angular/core";
import { CdkTreeModule, FlatTreeControl } from "@angular/cdk/tree";
import { ArrayDataSource } from "@angular/cdk/collections";
import { trackByIndexFunction } from "core/functions";
import { BodyHeightService } from "common/menu/body-height.service";
import { BehaviorSubject, filter, Subscription } from "rxjs";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ButtonExpandCollapseComponent } from "common/buttons/button-expand-collapse.component";
import { NavigationEnd, Router, RouterLink } from "@angular/router";
import { SwitchComponent } from "common/switch/switch.component";
import { ClickStopPropagationDirective } from "common/click-stop-propagation.directive";

@Component({
	selector: "stages-menu",
	templateUrl: "./menu.component.html",
	styleUrls: ["./menu.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgClass,
		TranslateModule,
		ButtonExpandCollapseComponent,
		NgTemplateOutlet,
		RouterLink,
		SwitchComponent,
		ClickStopPropagationDirective,
		CdkTreeModule,
	],
})
export class MenuComponent implements OnChanges, OnDestroy, OnInit {
	private bodyHeightService = inject(BodyHeightService);
	private changeDetector = inject(ChangeDetectorRef);
	private elRef = inject(ElementRef);
	private router = inject(Router);

	treeControl = new FlatTreeControl<MenuItem>(
		(node) => node.level,
		(node) => node.expandable,
	);

	readonly menuId = input<string | null>();

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input()
	menuTitle?: string;

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input()
	subtitle?: string;

	readonly classes = input<Set<string> | string[] | StringToUnknown | string>();

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input()
	iconClasses?: Set<string> | string[] | StringToUnknown | string;

	readonly slim = input(false);

	readonly openTo = input<string>();

	readonly noCollapse = input<boolean>();

	private filteredItems$ = new BehaviorSubject<MenuItem[]>([]);

	filteredItems: MenuItem[] = [];

	dataSource: ArrayDataSource<MenuItem>;

	finishedLoading: boolean = false;

	readonly items = input<MenuItem[]>();

	readonly isToolbarDropdown = input<boolean>(false);

	// TODO: Skipped for migration because:
	//  This input is used in combination with `@HostBinding` and migrating would
	//  break.
	@Input()
	@HostBinding("class.enableOverflowOutbreak")
	enableOverflowOutbreak = false;

	readonly context = input<unknown>();

	readonly keepOpenOnMenuClick = input<boolean>();

	readonly titleIconClass = input<string>();

	readonly titleParams = input<string[]>();

	readonly buttonNameKey = input<string>("contextmenu");

	constructor() {
		this.dataSource = new ArrayDataSource<MenuItem>(this.filteredItems$.asObservable());
	}

	routerEventsSubscription?: Subscription;

	ngOnInit(): void {
		this.routerEventsSubscription = this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe((_event: NavigationEnd) => {
				this.closeMenu();
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		const usedItems = changes.items?.currentValue ?? this.items();
		if (usedItems?.length > 0) {
			const usedContext = changes.context?.currentValue ?? this.context();
			this.filteredItems = getFilteredItems(usedItems, usedContext);
		} else {
			this.filteredItems = [];
		}
		this.filteredItems$.next(this.filteredItems);
		this.finishedLoading = true;
	}

	trackByIndex = trackByIndexFunction;

	open = false;
	private justOpened = false;
	hasTitle = false;

	toggle($event: MouseEvent): void {
		if (this.open) {
			this.open = false;
			this.justOpened = false;
			document.removeEventListener("click", this.closeHandler);
			const menuId = this.menuId();
			if (menuId) {
				this.bodyHeightService.unsetHeight(menuId);
			}
		} else {
			this.hasTitle = !!this.menuTitle;
			this.open = true;
			this.justOpened = true;
			document.addEventListener("click", this.closeHandler);
			const menuId = this.menuId();
			if (menuId) {
				// compute the height of the menu and register it
				// this is to handle a Chrome bug (see ST-34423)
				// note: the item height is assumed to be 40px here
				// this must be kept in sync with menu.scss (see .menu .mi)
				this.bodyHeightService.setHeight(
					menuId,
					($event.target! as HTMLElement).getBoundingClientRect().top +
						window.pageYOffset +
						this.filteredItems.length * 40 -
						20,
				);
			}
		}
	}

	ignoreClickOnMenuItem(element: ElementRef): boolean {
		return !!this.keepOpenOnMenuClick() && element.nativeElement.closest("ul.menu")?.length > 0;
	}

	ignoreClickOnSwitch(element: ElementRef): boolean {
		return element.nativeElement.closest(".switch-item")
			? element.nativeElement.closest(".switch-item").length > 0
			: false;
	}

	hasChild(_: number, node: MenuItem): boolean {
		return node.expandable;
	}

	getParentNode(node: MenuItem): MenuItem {
		const nodeIndex = this.filteredItems.findIndex((item: MenuItem) => item.id === node.id);
		for (let i = nodeIndex - 1; i >= 0; i--) {
			if (this.filteredItems[i].level === node.level - 1) {
				return this.filteredItems[i];
			}
		}

		return null;
	}

	shouldRender(node: MenuItem): boolean {
		let parent = this.getParentNode(node);
		while (parent) {
			if (!parent.isExpanded) {
				return false;
			}
			parent = this.getParentNode(parent);
		}
		return true;
	}

	private closeHandler: EventListener = () => {
		if (
			this.open &&
			!this.justOpened &&
			!this.ignoreClickOnMenuItem(this.elRef) &&
			!this.ignoreClickOnSwitch(this.elRef)
		) {
			setTimeout(() => this.closeMenu(), 0);
		}
		this.justOpened = false;
	};

	closeMenu(): void {
		if (this.open) {
			this.open = false;
			document.removeEventListener("click", this.closeHandler);
			const menuId = this.menuId();
			if (menuId) {
				this.bodyHeightService.unsetHeight(menuId);
			}

			if (this.changeDetector && !(this.changeDetector as ViewRef).destroyed) {
				this.changeDetector.detectChanges();
			}
		}
	}

	collapseMenu(filteredItems: MenuItem[]): boolean {
		if (this.noCollapse()) {
			return false;
		}
		if (filteredItems.length > 1) {
			return false;
		}
		return filteredItems.length === 1 && !filteredItems[0].isSwitch;
	}

	isFunction(prop: unknown): boolean {
		return typeof prop === "function";
	}

	ngOnDestroy(): void {
		this.closeMenu();
		if (this.routerEventsSubscription) {
			this.routerEventsSubscription.unsubscribe();
		}
	}
}

function getFilteredItems(items: MenuItem[] | undefined, context: unknown): MenuItem[] {
	let filteredItems: MenuItem[] = [];
	if (!items) {
		return filteredItems;
	}

	let lastItem: MenuItem;

	// needed to tell storybook that it is an array and not an object
	const copydItems = Array.from(items);
	filteredItems = copydItems.filter((item) => {
		let shouldBeAdded = true;

		if (item.isSeparator && lastItem?.isSeparator) {
			shouldBeAdded = false;
		}

		let isEnabled = true;

		if (!item.hasOwnProperty("disabled")) {
			isEnabled = true;
		} else if (typeof item.disabled === "boolean") {
			isEnabled = !item.disabled;
		} else if (typeof item.disabled === "function") {
			isEnabled = !item.disabled(context);
		} else if (!item.isSeparator) {
			isEnabled = false;
		}

		if (shouldBeAdded && isEnabled) {
			lastItem = item;
			return true;
		}
		return false;
	});

	// remove last item if it's delimiter
	if (filteredItems.length > 0 && filteredItems[filteredItems.length - 1].isSeparator) {
		filteredItems.splice(-1, 1);
	}

	// remove first item if it's delimiter
	if (filteredItems.length > 0 && filteredItems[0].isSeparator) {
		filteredItems.splice(0, 1);
	}

	// empty menu items when only separators are withing
	if (filteredItems.filter((item) => item.isSeparator).length === filteredItems.length) {
		return [];
	}
	return filteredItems;
}
